import { assign, createMachine } from "xstate";
import { getMsisdnNumbersForReservation } from "@nju/data-access-portal-msisdn";
import {
  fetchOrchestrators,
  IFtthOrchestrator,
  IMobileOrchestrator,
  IMultiOrderOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { createMultiOrderMsisdnOrder } from "@nju/data-access-portal-order";
import { ContractError } from "@nju/result";
import { IScope } from "@nju/scope";

export interface INumbers {
  id?: string;
  value: string;
  msisdnToken?: string;
}

interface IContext {
  offerId?: string;
  numbers: INumbers[];
  variant: IScope;
  orchestrator?:
    | IMobileOrchestrator
    | IFtthOrchestrator
    | IMultiOrderOrchestrator;
  errorMessage?: string;
  selectedNumber: string;
}

type IEvent =
  | { type: "SET_SELECTED_NUMBER"; value: string }
  | { type: "FETCH_NEW_NUMBERS" }
  | {
      type: "DONE";
      orchestrator: IMultiOrderOrchestrator;
    }
  | { type: "SUBMIT" }
  | { type: "NUMBERS"; numbers: INumbers[] }
  | { type: "FETCH_ERROR" }
  | { type: "ERROR"; errorMessage?: string }
  | { type: "ANOTHER_ORDER_EXISTS" };

export const chooseNewNumberMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "loading",
    states: {
      loading: {
        entry: "clearSelectedNumber",
        invoke: {
          src: "loading",
        },
        on: {
          NUMBERS: {
            actions: "setNumbers",
            target: "ready",
          },
          FETCH_ERROR: {
            target: "noDataScreen",
          },
        },
      },
      ready: {
        after: {
          300_000: {
            target: "phoneNumbersExpired",
          },
        },
        on: {
          SET_SELECTED_NUMBER: {
            actions: "setSelectedNumber",
          },
          FETCH_NEW_NUMBERS: {
            target: "loading",
          },
          SUBMIT: [
            {
              cond: "checkIfNumberSelected",
              target: "newOrder",
            },
            {
              actions: "setErrorMessage",
            },
          ],
        },
      },
      newOrder: {
        invoke: {
          src: "createOrder",
        },
        on: {
          DONE: {
            actions: "onSuccess",
            type: "final",
          },
          ANOTHER_ORDER_EXISTS: {
            actions: "redirectToAnotherOrderExists",
          },
          ERROR: "error",
        },
      },
      phoneNumbersExpired: {
        on: {
          SET_SELECTED_NUMBER: {
            actions: "setSelectedNumber",
          },
          FETCH_NEW_NUMBERS: {
            target: "loading",
          },
          SUBMIT: [
            {
              cond: "checkIfNumberSelected",
              target: "expiredScreen",
            },
            {
              actions: "setErrorMessage",
            },
          ],
        },
      },
      expiredScreen: {
        on: {
          FETCH_NEW_NUMBERS: {
            target: "loading",
          },
        },
      },
      noDataScreen: {
        on: {
          FETCH_NEW_NUMBERS: {
            target: "loading",
          },
        },
      },
      error: {
        type: "final",
      },
    },
  },
  {
    actions: {
      setSelectedNumber: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_SELECTED_NUMBER") {
          return {
            selectedNumber: event.value,
            errorMessage: undefined,
          };
        }

        return context;
      }),
      clearSelectedNumber: assign<IContext, IEvent>((_, __) => {
        return {
          selectedNumber: undefined,
        };
      }),
      setNumbers: assign<IContext, IEvent>((context, event) => {
        if (event.type === "NUMBERS") {
          //Domyślnie zaznaczony środkowy numer z listy
          const defaultSelectedNumber =
            event.numbers.length > 2
              ? event.numbers[event.numbers.length / 2 - 1]
              : event.numbers[0];

          return {
            numbers: event.numbers,
            selectedNumber: defaultSelectedNumber.msisdnToken,
          };
        }

        return context;
      }),
      setErrorMessage: assign<IContext, IEvent>((_, __) => {
        return {
          errorMessage: "wybierz numer, aby przejść dalej",
        };
      }),
    },
    services: {
      loading: () => {
        return async (callback) => {
          const result = await getMsisdnNumbersForReservation();

          if (result.isError || result.value.numbers.length === 0) {
            return callback({ type: "FETCH_ERROR" });
          }

          return callback({
            type: "NUMBERS",
            numbers: result.value.numbers,
          });
        };
      },
      createOrder: ({ selectedNumber, offerId }) => {
        return async (callback) => {
          try {
            const { multiOrder } = (await fetchOrchestrators()).unwrap();

            if (!multiOrder) {
              throw new ContractError({
                message: "`group Orchestrator` is missing",
              });
            }

            if (!multiOrder.id) {
              throw new ContractError({
                message: "`groupOrchestrator.id` is missing",
              });
            }

            if (!multiOrder?.userId) {
              throw new Error("missing groupOrchestrator?.payload?.userId");
            }

            if (selectedNumber === null) {
              throw new ContractError({
                message: "`msisdnToken` cannot be null",
              });
            }

            if (!multiOrder?.id) {
              throw new ContractError({
                message: "`orchestrator.id` is missing",
              });
            }

            if (!offerId) {
              throw new ContractError({
                message: "`offerId` is missing",
              });
            }

            const result = await createMultiOrderMsisdnOrder({
              mode: "mobile",
              msisdnToken: selectedNumber,
              orchestratorId: multiOrder.id,
              processId: "NEW_NUMBER",
            });

            if (
              result.isError &&
              result.err.code === "ANOTHER_ORDER_IN_PROGRESS"
            ) {
              return callback({ type: "ANOTHER_ORDER_EXISTS" });
            }

            if (result.isError) {
              return callback({ type: "ERROR" });
            }

            callback({
              type: "DONE",
              orchestrator: result.value,
            });
          } catch {
            callback({
              type: "ERROR",
            });
          }
        };
      },
    },
    guards: {
      checkIfNumberSelected: ({ selectedNumber }) => {
        return selectedNumber !== undefined;
      },
    },
  },
);
